// frontend/src/pages/Reconciliation.js
import React, { useState, useEffect } from 'react';
import { api } from '../App';

const CHANNELS = [
  { key: '',            label: 'All channels' },
  { key: 'walmart',     label: 'Walmart' },
  { key: 'amazon',      label: 'Amazon' },
  { key: 'ebay_source', label: 'EO eBay' },
  { key: 'ebay_store2', label: 'TNT eBay' },
];

const KINDS = [
  { key: '',                 label: 'All kinds' },
  { key: 'qty_drift',        label: 'Qty drift' },
  { key: 'price_drift',      label: 'Price drift' },
  { key: 'missing_remote',   label: 'Missing remote' },
  { key: 'orphan_remote',    label: 'Orphan remote' },
  { key: 'status_mismatch',  label: 'Status mismatch' },
  { key: 'run_error',        label: 'Run error' },
];

const SEVERITIES = [
  { key: '',        label: 'All severities' },
  { key: 'high',    label: 'High' },
  { key: 'medium',  label: 'Medium' },
  { key: 'low',     label: 'Low' },
];

export default function Reconciliation() {
  const [findings, setFindings] = useState([]);
  const [total, setTotal]       = useState(0);
  const [latest, setLatest]     = useState(null);
  const [running, setRunning]   = useState(false);
  const [filters, setFilters]   = useState({ channel: '', kind: '', severity: '', resolved: 'false' });

  const load = () => {
    const params = {};
    if (filters.channel)  params.channel = filters.channel;
    if (filters.kind)     params.kind = filters.kind;
    if (filters.severity) params.severity = filters.severity;
    if (filters.resolved) params.resolved = filters.resolved;
    api.get('/reconciliation/findings', { params }).then(r => {
      setFindings(r.data.findings || []);
      setTotal(r.data.total || 0);
      setLatest(r.data.latest || null);
    });
  };

  useEffect(() => { load(); }, [filters.channel, filters.kind, filters.severity, filters.resolved]);

  const runNow = async () => {
    if (!window.confirm('Run reconciliation now? Pulls all listings from Walmart + eBay. May take 1-2 minutes.')) return;
    setRunning(true);
    try {
      const res = await api.post('/reconciliation/run');
      alert(`Done. ${res.data.total} findings (Walmart: ${res.data.breakdown.walmart}, EO: ${res.data.breakdown.ebay_source}, TNT: ${res.data.breakdown.ebay_store2})`);
      load();
    } catch (e) {
      alert('Failed: ' + (e.response?.data?.error || e.message));
    } finally {
      setRunning(false);
    }
  };

  const resolve = async (id) => {
    await api.post(`/reconciliation/findings/${id}/resolve`);
    load();
  };

  const sevColor = { high: 'var(--danger)', medium: '#f59e0b', low: 'var(--muted)' };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">🔍 Reconciliation</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-ghost" onClick={load}>Refresh</button>
          <button className="btn btn-primary" onClick={runNow} disabled={running}>
            {running ? 'Running…' : 'Run now'}
          </button>
        </div>
      </div>

      {latest && (
        <div className="card" style={{ padding: 12, marginBottom: 12 }}>
          <div style={{ fontSize: 12, color: 'var(--muted)' }}>
            Latest run: <code>{latest.run_id}</code> — {latest.total} findings ({latest.open} open) — started {latest.started_at ? new Date(latest.started_at).toLocaleString() : ''}
          </div>
        </div>
      )}

      <div className="card" style={{ padding: 12, marginBottom: 12, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        {[
          { field: 'channel', opts: CHANNELS },
          { field: 'kind', opts: KINDS },
          { field: 'severity', opts: SEVERITIES },
          { field: 'resolved', opts: [
            { key: 'false', label: 'Open only' },
            { key: 'true',  label: 'Resolved only' },
            { key: '',      label: 'All' },
          ]},
        ].map(f => (
          <select key={f.field}
            value={filters[f.field]}
            onChange={e => setFilters({ ...filters, [f.field]: e.target.value })}>
            {f.opts.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        ))}
        <span style={{ alignSelf: 'center', color: 'var(--muted)', fontSize: 12 }}>{total} total</span>
      </div>

      <div className="card">
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Channel</th>
                <th>Kind</th>
                <th>Sev</th>
                <th>SKU</th>
                <th>Master</th>
                <th>Remote</th>
                <th>Note</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {findings.map(f => (
                <tr key={f.id} style={{ opacity: f.resolved_at ? 0.45 : 1 }}>
                  <td style={{ fontSize: 12, color: 'var(--muted)' }}>{new Date(f.created_at).toLocaleString()}</td>
                  <td style={{ fontSize: 12 }}>{f.channel}</td>
                  <td style={{ fontSize: 12 }}>{f.kind}</td>
                  <td style={{ fontSize: 12, color: sevColor[f.severity], fontWeight: 600 }}>{f.severity}</td>
                  <td style={{ fontFamily: 'monospace', fontSize: 11 }}>{f.sku || '—'}</td>
                  <td style={{ fontSize: 12 }}>{f.master_value ?? '—'}</td>
                  <td style={{ fontSize: 12 }}>{f.remote_value ?? '—'}</td>
                  <td style={{ fontSize: 11, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.note || ''}</td>
                  <td>
                    {f.resolved_at
                      ? <span style={{ fontSize: 11, color: 'var(--muted)' }}>resolved</span>
                      : <button className="btn btn-ghost" style={{ fontSize: 11, padding: '2px 8px' }}
                          onClick={() => resolve(f.id)}>✓</button>}
                  </td>
                </tr>
              ))}
              {findings.length === 0 && (
                <tr><td colSpan={9} style={{ textAlign: 'center', padding: 24, color: 'var(--muted)' }}>No findings match these filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
